import loadImage from 'blueimp-load-image'
import dayjs from 'dayjs'
import { ImageInfo } from './BlockUpload'
import { AppCategory, Monument } from '../../../types'

function parseExifLocation(data: any) {
  function dmsToDecimal(dmsStr: string, ref: string) {
    if (!dmsStr) return null;
    const [d, m, s] = dmsStr.split(",").map(Number);
    let decimal = d + m / 60 + s / 3600;
    if (ref === "S" || ref === "W") decimal = -decimal;
    return Number(decimal.toFixed(4));
  }

  const gps = data.GPSInfo || {};
  const latitude = dmsToDecimal(gps.GPSLatitude, gps.GPSLatitudeRef);
  const longitude = dmsToDecimal(gps.GPSLongitude, gps.GPSLongitudeRef);
  const heading = Number(gps.GPSImgDirection) ? Number(parseFloat(gps.GPSImgDirection).toFixed(2)) : undefined;

  return {
    latitude,
    longitude,
    heading,
  }
}

type ExifData = {
  date: string | null
  latitude: number | null
  longitude: number | null
  heading?: number | null
}

type ExifDataWithFile = ExifData & {
  file: File
}


function readExifData(file: File): Promise<ExifData | null> {
  return new Promise((resolve, reject) => {
    loadImage.parseMetaData(file, (data) => {
      if (data.exif) {
        const exif = data.exif as any
        const allProperties = exif.getAll()
        const dateFromExif = allProperties?.Exif?.DateTimeOriginal
        const { latitude, longitude, heading } = parseExifLocation(allProperties)
        resolve({
          date: dateFromExif ? dayjs(dateFromExif, 'YYYY:MM:DD HH:mm:ss').format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
          latitude,
          longitude,
          heading
        })

        
      } else {
        resolve(null)
      }
    })
  })
}

function capitalizeFirstLetter(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export async function processUploadFiles(
  fileList: FileList | File,
  monument: Monument,
  categories: AppCategory[] | undefined,
  default_format_of_image_name?: string
) {
  const images: ImageInfo[] = []

  const awaits: Array<Promise<ExifDataWithFile>> = []

  const targetList = fileList instanceof FileList ? Array.from(fileList) : [fileList]


  for (let i = 0; i < targetList.length; i++) {
    const file = targetList[i]
    const today = dayjs().format('YYYY-MM-DD')
    awaits.push(
      readExifData(targetList[i]).then((exifData) => {
        if (exifData) {
          return { ...exifData, file }
        } else {
          return { date: today, latitude: null, longitude: null, heading: null, file }
        }
      })
    )
  }

  const exifData = await Promise.all(awaits)

  for (let i = 0; i < exifData.length; i++) {
    const { date, latitude, longitude, heading, file } = exifData[i]
    
    const app_category = categories?.find(
      (c) => c.id === monument.app_category
    )
    let monumentPrefix =
     app_category?.is_municipality
        ? `Comune di ${monument?.label}`
        : `${monument?.label}`
    monumentPrefix = capitalizeFirstLetter(monumentPrefix)
    let titlePrefix = default_format_of_image_name
      ? default_format_of_image_name
          .replace('$GEO_L4_NAME$', monument?.location || '')
          .replace('$GEO_L3_NAME$', monument?.municipality_label || '')
          .replace('$GEO_L2_NAME$', monument?.province_label || '')
          .replace('$MONUMENT_NAME$', monument?.label || '')
          .replace('$TIMESTAMP$', dayjs().format('YYYY-MM-DD_HH-mm-ss'))
      : monument?.municipality_label
      ? `${monument?.municipality_label} - ${monumentPrefix}`
      : `${monumentPrefix}`
    images.push({
      title: `${titlePrefix}_${(
        i + 1
      )
        .toString()
        .padStart(3, '0')}`,
      description: monument?.label || '',
      file: file,
      date: date || dayjs().format('YYYY-MM-DD'),
      latitude: latitude || null,
      longitude: longitude || null,
      heading: heading || null,
      monument_id: monument?.id,
    })
  }
  return images
}
